import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { usersApi } from '../services/api';
import type { User as UserType, Review, Job, Skill } from '../types';
import { Avatar } from '../components/ui/Avatar';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Skeleton } from '../components/ui/Skeleton';
import { MaterialIcon } from '../components/ui/MaterialIcon';
import { formatDate } from '../utils/helpers';

interface ApiUser extends UserType {
  received_reviews?: Review[];
}

interface ProfileStats {
  completed_jobs: number;
  in_progress_jobs?: number;
  total_proposals?: number;
  published_jobs?: number;
  open_jobs?: number;
  total_reviews: number;
  rating_average: number;
}

const num = (v: number | string | undefined) => (typeof v === 'number' ? v : parseFloat(v as string) || 0);

// Calca ScreenPerfil de screens-main.jsx (Claude Design)
export const Profile: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { user: authUser, updateProfile, logout } = useAuthStore();

  const [profileUser, setProfileUser] = useState<UserType | null>(null);
  const [stats, setStats] = useState<ProfileStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    bio: '',
    city: '',
    state: '',
    address: '',
  });

  const isOwnProfile = !id || (authUser && authUser.id.toString() === id);
  const user = profileUser as ApiUser | null;

  useEffect(() => {
    const userId = id ? Number(id) : authUser?.id;
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    usersApi
      .get(userId)
      .then((res) => {
        setProfileUser(res.data.user);
        setStats(res.data.stats);
      })
      .catch(() => navigate('/'))
      .finally(() => setLoading(false));
  }, [id, authUser?.id, navigate]);

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        bio: user.bio || '',
        city: user.city || '',
        state: user.state || '',
        address: user.address || '',
      });
    }
  }, [user]);

  const handleLogout = async () => {
    if (!window.confirm('¿Cerrar sesión?')) return;
    await logout();
    navigate('/');
  };

  const handleSave = async () => {
    if (!isOwnProfile) return;
    setSaving(true);
    try {
      await updateProfile(formData);
      setIsEditing(false);
      if (authUser) {
        const res = await usersApi.get(authUser.id);
        setProfileUser(res.data.user);
        setStats(res.data.stats);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-xl mx-auto px-4 py-8 space-y-4">
        <Skeleton className="w-24 h-24 rounded-full mx-auto" />
        <Skeleton className="h-6 w-48 mx-auto" />
        <Skeleton className="h-20 rounded-2xl" />
      </div>
    );
  }

  if (!user) {
    return <div className="min-h-screen flex items-center justify-center text-onSurface-mute">Usuario no encontrado</div>;
  }

  const isWorker = user.user_type === 'worker';
  const isClient = user.user_type === 'client';

  const statItems = isWorker
    ? [{ l: 'Trabajos', v: stats?.completed_jobs }, { l: 'Reviews', v: stats?.total_reviews }, { l: 'Rating', v: stats ? num(stats.rating_average).toFixed(1) : undefined, star: true }]
    : [{ l: 'Publicados', v: stats?.published_jobs }, { l: 'Completados', v: stats?.completed_jobs }, { l: 'Rating', v: stats ? num(stats.rating_average).toFixed(1) : undefined, star: true }];

  const menu = [
    { icon: 'favorite', label: 'Favoritos', color: '#e03131' },
    { icon: 'history', label: 'Historial', color: '#1971c2' },
    { icon: 'star', label: 'Mis reseñas', value: String(user.reviews_count || 0), color: '#f59e0b', to: '#reviews' },
    { icon: 'payments', label: 'Métodos de pago', color: '#2f9e44' },
    { icon: 'notifications', label: 'Notificaciones', value: 'Activas', color: '#fe701a' },
    { icon: 'help', label: 'Ayuda y soporte', color: '#5a5347' },
  ];

  return (
    <div className="bg-surface-dim min-h-full pb-10">
      <div className="max-w-xl mx-auto">
        {/* Header centrado */}
        <div className="px-5 pt-6 pb-2 text-center">
          <div className="relative inline-block mb-3.5">
            <Avatar src={user.avatar} name={user.full_name} size="xl" />
            {isOwnProfile && (
              <button className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-primary-500 text-white border-[3px] border-surface-dim flex items-center justify-center">
                <MaterialIcon name="edit" size={14} />
              </button>
            )}
          </div>
          <div className="flex items-center justify-center gap-1.5">
            <h1 className="text-[22px] font-extrabold tracking-tight text-onSurface">{user.full_name}</h1>
            {user.verification_status === 'approved' && <MaterialIcon name="verified" size={20} fill className="text-info" />}
          </div>
          <p className="text-[13px] text-onSurface-var mt-1">
            {isWorker ? 'Profesional' : isClient ? 'Cliente' : 'Administrador'}
            {user.city ? ` · ${user.city}` : ''}
          </p>

          {isOwnProfile ? (
            <div className="flex justify-center gap-2 mt-3.5">
              <Button variant="outlined" size="sm" onClick={() => (isEditing ? handleSave() : setIsEditing(true))} isLoading={saving}>
                <MaterialIcon name="edit" size={16} /> {isEditing ? 'Guardar' : 'Editar perfil'}
              </Button>
              <Button variant="tonal" size="sm">
                <MaterialIcon name="share" size={16} /> Compartir
              </Button>
            </div>
          ) : (
            <p className="text-xs text-onSurface-mute mt-1">
              Miembro desde {user.created_at ? formatDate(user.created_at) : ''}
            </p>
          )}
        </div>

        {/* Banner de verificación para workers no aprobados */}
        {isOwnProfile && isWorker && user.worker_profile?.profile_status !== 'verified' && (
          <button
            onClick={() => navigate('/worker/profile')}
            className="mx-4 mt-3 w-[calc(100%-2rem)] flex items-center gap-3 p-4 rounded-2xl bg-warning-50 border border-warning-200 hover:border-warning-300 transition-colors text-left"
          >
            <MaterialIcon name="warning" size={20} className="text-warning-600 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-warning-800">Completá tu verificación profesional</p>
              <p className="text-xs text-warning-700">Necesitamos algunos datos más para activar tu perfil público.</p>
            </div>
            <MaterialIcon name="chevron_right" size={18} className="text-warning-600 shrink-0" />
          </button>
        )}

        {/* Stats */}
        <div className="mx-4 mt-4 p-4 bg-surface border border-outlineVarWarm rounded-2xl flex">
          {statItems.map((s, i) => (
            <React.Fragment key={s.l}>
              {i > 0 && <div className="w-px bg-outlineVarWarm my-0.5" />}
              <div className="flex-1 text-center">
                <p className="text-[22px] font-extrabold text-onSurface tracking-tight inline-flex items-center gap-1 leading-none">
                  {s.star && <MaterialIcon name="star" size={17} fill className="text-warning-500" />}
                  {s.v ?? '—'}
                </p>
                <p className="text-[11px] font-medium text-onSurface-mute uppercase tracking-wide mt-1">{s.l}</p>
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Edit form */}
        {isEditing && isOwnProfile && (
          <div className="mx-4 mt-4 p-5 bg-surface border border-outlineVarWarm rounded-2xl space-y-4">
            <h3 className="font-semibold text-onSurface">Editar perfil</h3>
            <div className="grid grid-cols-2 gap-3">
              <Input pill label="Nombre" value={formData.first_name} onChange={(e) => setFormData((d) => ({ ...d, first_name: e.target.value }))} />
              <Input pill label="Apellido" value={formData.last_name} onChange={(e) => setFormData((d) => ({ ...d, last_name: e.target.value }))} />
            </div>
            <Input pill label="Teléfono" value={formData.phone} onChange={(e) => setFormData((d) => ({ ...d, phone: e.target.value }))} />
            <div className="grid grid-cols-2 gap-3">
              <Input pill label="Ciudad" value={formData.city} onChange={(e) => setFormData((d) => ({ ...d, city: e.target.value }))} />
              <Input pill label="Provincia" value={formData.state} onChange={(e) => setFormData((d) => ({ ...d, state: e.target.value }))} />
            </div>
            <Input pill label="Dirección" value={formData.address} onChange={(e) => setFormData((d) => ({ ...d, address: e.target.value }))} />
            <div>
              <label className="text-xs font-bold text-onSurface-var uppercase tracking-wide mb-2 block">Biografía</label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData((d) => ({ ...d, bio: e.target.value }))}
                rows={4}
                className="w-full rounded-xl border border-outlineWarm bg-surface p-3.5 text-sm text-onSurface outline-none focus:border-primary-500 resize-none"
              />
            </div>
          </div>
        )}

        {/* Bio */}
        {!isEditing && (user.bio || isWorker) && (
          <div className="mx-4 mt-4 p-4 bg-surface border border-outlineVarWarm rounded-2xl">
            <p className="text-[12px] font-bold text-onSurface-var uppercase tracking-wide mb-2">Sobre mí</p>
            <p className="text-sm text-onSurface leading-relaxed">{user.bio || 'Todavía no escribió una biografía.'}</p>
          </div>
        )}

        {/* Skills */}
        {user.skills && user.skills.length > 0 && (
          <div className="mx-4 mt-4 p-4 bg-surface border border-outlineVarWarm rounded-2xl">
            <p className="text-[12px] font-bold text-onSurface-var uppercase tracking-wide mb-2.5">Habilidades</p>
            <div className="flex flex-wrap gap-2">
              {user.skills.map((skill: Skill) => (
                <span key={skill.id} className="px-3 py-1.5 bg-cream text-primary-800 rounded-full text-xs font-medium">
                  {skill.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Portfolio */}
        {isWorker && user.portfolios && user.portfolios.length > 0 && (
          <div className="mx-4 mt-4 p-4 bg-surface border border-outlineVarWarm rounded-2xl">
            <p className="text-[12px] font-bold text-onSurface-var uppercase tracking-wide mb-2.5">Proyectos destacados</p>
            <div className="grid grid-cols-2 gap-3">
              {user.portfolios.map((portfolio) => (
                <div key={portfolio.id} className="border border-outlineVarWarm rounded-xl p-3">
                  <h3 className="text-sm font-semibold text-onSurface mb-1 line-clamp-1">{portfolio.title}</h3>
                  {portfolio.description && <p className="text-xs text-onSurface-mute line-clamp-2">{portfolio.description}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reviews */}
        {user.received_reviews && user.received_reviews.length > 0 && (
          <div id="reviews" className="mx-4 mt-4 p-4 bg-surface border border-outlineVarWarm rounded-2xl">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[12px] font-bold text-onSurface-var uppercase tracking-wide">Calificaciones</p>
              <span className="text-xs text-onSurface-mute">{user.received_reviews.length}</span>
            </div>
            <div className="space-y-4">
              {user.received_reviews.map((review) => (
                <div key={review.id} className="border-b border-outlineVarWarm last:border-0 pb-4 last:pb-0">
                  <div className="flex items-center gap-2.5">
                    <Avatar src={review.reviewer?.avatar} name={review.reviewer?.full_name} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-onSurface truncate">{review.reviewer?.full_name}</p>
                      {review.job && <p className="text-xs text-onSurface-mute truncate">{review.job.title}</p>}
                    </div>
                    <span className="inline-flex items-center gap-0.5 text-xs text-onSurface-mute shrink-0">
                      <MaterialIcon name="star" size={12} fill className="text-warning-500" />
                      {review.rating}
                    </span>
                  </div>
                  {review.comment && <p className="text-sm text-onSurface-var mt-2">&ldquo;{review.comment}&rdquo;</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Trabajos publicados (cliente) */}
        {isClient && user.jobs && user.jobs.length > 0 && (
          <div className="mx-4 mt-4 p-4 bg-surface border border-outlineVarWarm rounded-2xl">
            <p className="text-[12px] font-bold text-onSurface-var uppercase tracking-wide mb-2.5">Trabajos publicados</p>
            <div className="space-y-2">
              {user.jobs.slice(0, 5).map((job: Job) => (
                <button
                  key={job.id}
                  onClick={() => navigate(`/jobs/${job.id}`)}
                  className="w-full flex items-center justify-between p-3 border border-outlineVarWarm rounded-xl hover:border-primary-300 transition-colors text-left"
                >
                  <span className="text-sm font-medium text-onSurface truncate">{job.title}</span>
                  <MaterialIcon name="chevron_right" size={18} className="text-onSurface-mute shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Contacto */}
        <div className="mx-4 mt-4 p-4 bg-surface border border-outlineVarWarm rounded-2xl space-y-3">
          <p className="text-[12px] font-bold text-onSurface-var uppercase tracking-wide">Información de contacto</p>
          <ContactRow icon="mail" label="Email" value={user.email} />
          <ContactRow icon="call" label="Teléfono" value={user.phone || 'No especificado'} />
          <ContactRow icon="location_on" label="Ubicación" value={[user.city, user.state, user.country].filter(Boolean).join(', ') || 'No especificado'} />
        </div>

        {/* Menu (solo perfil propio) */}
        {isOwnProfile && (
          <div className="mx-4 mt-4 bg-surface border border-outlineVarWarm rounded-2xl overflow-hidden">
            {menu.map((it, i) => (
              <a
                key={it.label}
                href={it.to || undefined}
                className={`w-full h-14 px-4 flex items-center gap-3.5 ${i < menu.length - 1 ? 'border-b border-outlineVarWarm' : ''} ${it.to ? '' : 'cursor-default'}`}
              >
                <span style={{ background: `${it.color}18`, color: it.color }} className="w-8 h-8 rounded-[10px] flex items-center justify-center shrink-0">
                  <MaterialIcon name={it.icon} size={18} />
                </span>
                <span className="flex-1 text-left text-sm font-medium text-onSurface">{it.label}</span>
                {it.value && <span className="text-xs text-onSurface-mute">{it.value}</span>}
                <MaterialIcon name="chevron_right" size={18} className="text-onSurface-mute" />
              </a>
            ))}
          </div>
        )}

        {isOwnProfile && (
          <div className="px-4 mt-5">
            <Button variant="outlined" size="md" fullWidth danger onClick={handleLogout}>
              <MaterialIcon name="logout" size={16} /> Cerrar sesión
            </Button>
            <p className="text-center text-[11px] text-onSurface-mute mt-4">Obros · v1.0.0</p>
          </div>
        )}
      </div>
    </div>
  );
};

const ContactRow: React.FC<{ icon: string; label: string; value: string }> = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <MaterialIcon name={icon} size={16} className="text-onSurface-mute mt-0.5" />
    <div className="min-w-0">
      <p className="text-[11px] text-onSurface-mute">{label}</p>
      <p className="text-sm text-onSurface truncate">{value}</p>
    </div>
  </div>
);
